import type { MasteryTier, SkillMap, SkillProgress } from '../types/mastery';
import { normalizeFutureSkills } from '../data/future-skills';

const TIER_THRESHOLDS: { tier: MasteryTier; min: number }[] = [
  { tier: 'mastered', min: 150 },
  { tier: 'proficient', min: 50 },
  { tier: 'learning', min: 0 },
];

const STAR_MULTIPLIER: Record<1 | 2 | 3, number> = { 1: 1, 2: 1.5, 3: 2 };
const DIFF_MULTIPLIER: Record<1 | 2 | 3, number> = { 1: 1, 2: 1.5, 3: 2 };

export function calculateMasteryPoints(stars: 1 | 2 | 3, difficulty: 1 | 2 | 3): number {
  return Math.round(10 * STAR_MULTIPLIER[stars] * DIFF_MULTIPLIER[difficulty]);
}

export function getTier(points: number): MasteryTier {
  for (const t of TIER_THRESHOLDS) {
    if (points >= t.min) return t.tier;
  }
  return 'learning';
}

export function getTierProgress(points: number): { tier: MasteryTier; progress: number; nextThreshold: number } {
  if (points >= 150) return { tier: 'mastered', progress: 1, nextThreshold: 150 };
  if (points >= 50) return { tier: 'proficient', progress: (points - 50) / 100, nextThreshold: 150 };
  return { tier: 'learning', progress: points / 50, nextThreshold: 50 };
}

function emptySkill(skillId: string, date: string): SkillProgress {
  return { skillId, points: 0, tier: 'learning', completionCount: 0, lastPracticedDate: date };
}

export function updateSkillMap(
  current: SkillMap,
  futureSkills: string[],
  subject: string,
  stars: 1 | 2 | 3,
  difficulty: 1 | 2 | 3,
  date: string,
): SkillMap {
  const points = calculateMasteryPoints(stars, difficulty);
  const updated = { ...current };

  const allKeys = [...normalizeFutureSkills(futureSkills), subject];
  for (const key of allKeys) {
    const existing = updated[key] || emptySkill(key, date);
    const newPoints = existing.points + points;
    updated[key] = {
      ...existing,
      points: newPoints,
      tier: getTier(newPoints),
      completionCount: existing.completionCount + 1,
      lastPracticedDate: date,
    };
  }

  return updated;
}

export function getWeakSkills(skillMap: SkillMap, count: number): string[] {
  const practiced = Object.values(skillMap).filter(s => s.completionCount > 0);
  if (practiced.length === 0) return [];
  return practiced
    .sort((a, b) => a.points - b.points)
    .slice(0, count)
    .map(s => s.skillId);
}

export function getSkillsBySubject(skillMap: SkillMap, subjects: string[]): SkillProgress[] {
  return subjects
    .map(s => skillMap[s])
    .filter((s): s is SkillProgress => !!s);
}

export function getAllSkillsSorted(skillMap: SkillMap): SkillProgress[] {
  return Object.values(skillMap).sort((a, b) => b.points - a.points);
}
